import { createSignal, createEffect, on, onMount, For, Show } from "solid-js";
import { api, type RefreshTokenInfo } from "../shared";
import { Pagination } from "./Pagination";

export function RefreshTokensTable(props: { organizationId?: string }) {
  const [tokens, setTokens] = createSignal<RefreshTokenInfo[]>([]);
  const [total, setTotal] = createSignal(0);
  const [page, setPage] = createSignal(1);
  const [perPage, setPerPage] = createSignal(20);
  const [loading, setLoading] = createSignal(false);
  const [extendId, setExtendId] = createSignal<string | null>(null);
  const [extendMinutes, setExtendMinutes] = createSignal(60);
  const [extendCustom, setExtendCustom] = createSignal("");

  async function load() {
    setLoading(true);
    try {
      const result = await api.refreshTokens.list({
        limit: perPage(),
        offset: (page() - 1) * perPage(),
        organizationId: props.organizationId,
      });
      setTokens(result.items || []);
      setTotal(result.total);
    } catch {
      setTokens([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  onMount(load);
  createEffect(on(() => props.organizationId, () => { setPage(1); load(); }));
  createEffect(() => { page(); perPage(); load(); });

  async function doRevoke(id: string) {
    try { await api.refreshTokens.revoke(id); load(); } catch { /* ignore */ }
  }

  async function doExtend() {
    const id = extendId();
    if (!id) return;
    const mins = extendCustom() ? parseInt(extendCustom()) : extendMinutes();
    if (!mins || mins <= 0) return;
    try { await api.refreshTokens.extend(id, mins); closeExtend(); load(); } catch { /* ignore */ }
  }

  function openExtend(id: string) {
    setExtendId(id);
    setExtendMinutes(60);
    setExtendCustom("");
  }

  function closeExtend() {
    setExtendId(null);
    setExtendMinutes(60);
    setExtendCustom("");
  }

  function quickExtend(minutes: number) {
    setExtendMinutes(minutes);
    setExtendCustom("");
  }

  function getExtendPreview(): string {
    const mins = extendCustom() ? parseInt(extendCustom()) : extendMinutes();
    if (!mins || mins <= 0) return "";
    const d = new Date(Date.now() + mins * 60_000);
    return d.toLocaleString();
  }

  return (
    <div class="space-y-3">
      <div class="flex items-center justify-between">
        <h2 class="text-lg font-semibold">Refresh Tokens ({total()})</h2>
      </div>
      <div class="border border-gray-800 rounded-lg overflow-x-auto max-h-80 overflow-y-auto">
        <table class="w-full text-sm">
          <thead class="bg-gray-900 text-gray-400 sticky top-0">
            <tr>
              <th class="text-left px-4 py-3 font-medium">User</th>
              <th class="text-left px-4 py-3 font-medium">Org</th>
              <th class="text-left px-4 py-3 font-medium">Created</th>
              <th class="text-left px-4 py-3 font-medium">Expires</th>
              <th class="text-left px-4 py-3 font-medium">Last Used</th>
              <th class="text-left px-4 py-3 font-medium">Status</th>
              <th class="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-800">
            <For each={tokens()}>
              {(t) => {
                const expired = new Date(t.expiresAt) < new Date();
                const status = t.revokedAt ? "Revoked" : expired ? "Expired" : "Active";
                const statusColor = t.revokedAt || expired ? "text-gray-500" : "text-emerald-400";
                return (
                  <tr class="hover:bg-gray-900/50">
                    <td class="px-4 py-3 text-gray-200">{t.userDisplayName || t.userEmail}</td>
                    <td class="px-4 py-3 text-gray-400 text-xs">{t.orgName || "—"}</td>
                    <td class="px-4 py-3 text-gray-500 text-xs">{new Date(t.createdAt).toLocaleString()}</td>
                    <td class="px-4 py-3 text-gray-500 text-xs">{new Date(t.expiresAt).toLocaleString()}</td>
                    <td class="px-4 py-3 text-gray-500 text-xs">{t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleString() : "—"}</td>
                    <td class="px-4 py-3"><span class={`text-xs ${statusColor}`}>{status}</span></td>
                    <td class="px-4 py-3 text-right space-x-1">
                      <Show when={!t.revokedAt}>
                        <button onClick={() => openExtend(t.id)} class="text-xs px-2 py-0.5 bg-cyan-900/30 border border-cyan-700 text-cyan-300 rounded hover:bg-cyan-900/50 cursor-pointer">Extend</button>
                      </Show>
                      <Show when={!t.revokedAt}>
                        <button onClick={() => doRevoke(t.id)} class="text-xs px-2 py-0.5 bg-red-900/30 border border-red-700 text-red-300 rounded hover:bg-red-900/50 cursor-pointer">Revoke</button>
                      </Show>
                    </td>
                  </tr>
                );
              }}
            </For>
          </tbody>
        </table>
      </div>
      <Pagination
        page={page()}
        totalPages={Math.ceil(total() / perPage())}
        perPage={perPage()}
        total={total()}
        loading={loading()}
        onPageChange={setPage}
        onPerPageChange={(pp) => { setPage(1); setPerPage(pp); }}
      />

      <Show when={extendId()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center">
          <div class="absolute inset-0 bg-black/60" onClick={closeExtend} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-sm shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-4">Extend Refresh Token</h3>
            <div class="space-y-4">
              <div>
                <label class="block text-sm text-gray-400 mb-1">Quick</label>
                <div class="flex gap-2">
                  <button onClick={() => quickExtend(60)} class={`px-3 py-1 text-xs rounded cursor-pointer ${extendMinutes() === 60 && !extendCustom() ? "bg-cyan-600 text-white" : "bg-gray-800 text-gray-300 hover:bg-gray-700"}`}>+1h</button>
                  <button onClick={() => quickExtend(720)} class={`px-3 py-1 text-xs rounded cursor-pointer ${extendMinutes() === 720 && !extendCustom() ? "bg-cyan-600 text-white" : "bg-gray-800 text-gray-300 hover:bg-gray-700"}`}>+12h</button>
                  <button onClick={() => quickExtend(1440)} class={`px-3 py-1 text-xs rounded cursor-pointer ${extendMinutes() === 1440 && !extendCustom() ? "bg-cyan-600 text-white" : "bg-gray-800 text-gray-300 hover:bg-gray-700"}`}>+24h</button>
                </div>
              </div>
              <div>
                <label class="block text-sm text-gray-400 mb-1">Custom (minutes)</label>
                <input
                  type="number"
                  min="1"
                  value={extendCustom()}
                  onInput={(e) => setExtendCustom(e.currentTarget.value)}
                  placeholder={String(extendMinutes())}
                  class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600"
                />
              </div>
              <Show when={getExtendPreview()}>
                <div class="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-xs text-cyan-300">
                  New expiry: {getExtendPreview()}
                </div>
              </Show>
            </div>
            <div class="flex justify-end gap-3 pt-4">
              <button onClick={closeExtend} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button>
              <button onClick={doExtend} disabled={!(extendCustom() ? parseInt(extendCustom()) : extendMinutes())} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 cursor-pointer">Extend</button>
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
}
